"use strict";
/**
 * The "request never reached the server" error type.
 *
 * Lives in its own module rather than in ./request.ts because auth.ts needs it
 * too, and request.ts already imports auth.ts — putting it here keeps that from
 * becoming an import cycle.
 *
 * Why this type exists at all: a bare `TypeError: Failed to fetch` is what the
 * browser gives you for offline, DNS failure, TLS failure, a captive portal,
 * and a network blocking the domain — and it is indistinguishable from an
 * application error once it has been caught and flattened into a boolean or a
 * generic string. The operator runs this app from a phone, where devtools are
 * not available, so the on-screen message is the only diagnostic they get.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NetworkError = void 0;
exports.isNetworkError = isNetworkError;
exports.networkErrorFrom = networkErrorFrom;
const network_failure_log_1 = require("./network-failure-log");
/** Thrown when the request never reached the server. */
class NetworkError extends Error {
    constructor(message, 
    /** The underlying fetch rejection message, kept for diagnosis. */
    detail, cause) {
        super(message);
        this.detail = detail;
        this.cause = cause;
        this.name = "NetworkError";
    }
}
exports.NetworkError = NetworkError;
/** True when the request never reached the server (vs. an error response). */
function isNetworkError(error) {
    return error instanceof NetworkError;
}
/**
 * Wrap a fetch rejection in a NetworkError with the canonical operator-facing
 * message. Keep the wording in one place so every call site agrees — the
 * message is the diagnostic.
 *
 * Also records the failure to the in-memory ring buffer so the connection-check
 * panel can show it later. This runs ONLY on the failure path, so it costs
 * nothing on a successful request — including on the order hot path.
 */
function networkErrorFrom(err, 
/** Request path, for the diagnostic log. Never pass headers or bodies. */
path, 
/** Measured time to failure, where the call site tracked it. */
durationMs) {
    const detail = err instanceof Error ? err.message : String(err);
    (0, network_failure_log_1.recordNetworkFailure)({
        at: Date.now(),
        path: path ?? "(unknown)",
        detail,
        durationMs,
    });
    // Short and plain on purpose. The browser's own reason ("Failed to fetch",
    // "Load failed") is opaque to a reader and only useful when analysing a
    // report, so it lives on `detail` and in the copy-diagnostics output rather
    // than on screen.
    return new NetworkError("Cannot reach server. Check your connection and try again.", detail, err);
}
